import React, { useState, useEffect, useRef } from 'react';
import { Copy, X, Clipboard } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import BaseModal, { contentVariants, messageVariants } from './BaseModal';
import { motion, AnimatePresence } from 'framer-motion';

interface IdentityModalProps {
  isOpen: boolean;
  onClose: () => void;
  userName: string;
  fromDonatePage?: boolean;
  onModalSubmit?: () => void;
}

interface Message {
  id: string;
  text: string;
  type: 'error' | 'info';
}

const IdentityModal: React.FC<IdentityModalProps> = ({
  isOpen,
  onClose,
  userName,
  fromDonatePage = false,
  onModalSubmit,
}) => {
  const [mode, setMode] = useState<'create' | 'login'>('create');
  const [newName, setNewName] = useState('');
  const [userId, setUserId] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const defaultName = 'El teu nom';
  const defaultId = 'El teu ID';
  const MAX_NAME_LENGTH = 20;
  const MAX_ID_LENGTH = 8;
  const [message, setMessage] = useState<Message | null>(null);
  const messageTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalContentRef = useRef<HTMLDivElement>(null);

  const addMessage = (text: string, type: 'error' | 'info') => {
    const newId = crypto.randomUUID();
    setMessage({ id: newId, text, type });
    if (messageTimeout.current) clearTimeout(messageTimeout.current);
    messageTimeout.current = setTimeout(() => setMessage(null), 4000);
  };

  // Cargar el nombre almacenado al montar el componente
  useEffect(() => {
    const storedName = localStorage.getItem('userName') || userName || '';
    setNewName(storedName);
    return () => {
      if (messageTimeout.current) clearTimeout(messageTimeout.current);
    };
  }, [userName]);

  // Reiniciar el modo a 'create' cuando el modal se abre
  useEffect(() => {
    if (isOpen) {
      setMode('create'); // Forzar 'create' al abrir
      setUserId(''); // Limpiar userId para consistencia
      setError(''); // Limpiar errores previos
      setMessage(null); // Limpiar mensajes previos
    }
  }, [isOpen]);

  const handleClose = () => {
    setNewName('');
    setUserId('');
    setError('');
    setIsSubmitting(false);
    setMessage(null);
    setMode('create'); // Reiniciar modo al cerrar
    onClose();
  };

  const handlePasteCode = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setUserId(clipboardText.slice(0, MAX_ID_LENGTH));
      addMessage('ID enganxat des del portapapers.', 'info');
    } catch (err) {
      addMessage("Error al enganxar. Permet l'accés al portapapers.", 'error');
    }
  };

  const handleAction = async (action: () => Promise<void>) => {
    setError('');
    if (!newName.trim()) {
      setError('Introdueix un nom');
      return;
    }
    if (newName.length > MAX_NAME_LENGTH) {
      setError(`El nom no pot tenir més de ${MAX_NAME_LENGTH} caràcters.`);
      return;
    }
    if (mode === 'login' && !userId.trim()) {
      addMessage('Introdueix un ID', 'error');
      return;
    }
    const userIdRegex = /^[0-9a-f]{8}$/i;
    if (mode === 'login' && !userIdRegex.test(userId)) {
      addMessage('Introdueix un ID vàlid.', 'error');
      return;
    }

    try {
      setIsSubmitting(true);
      localStorage.setItem('userName', newName);
      window.dispatchEvent(
        new CustomEvent('userNameChanged', { detail: { name: newName } })
      );

      if (mode === 'login' || !localStorage.getItem('userId')) {
        const id =
          mode === 'login' ? userId : crypto.randomUUID().substring(0, 8);
        localStorage.setItem('userId', id);
        window.dispatchEvent(
          new CustomEvent('userIdChanged', { detail: { id } })
        );
      }

      await action();
      handleClose();

      if (fromDonatePage && onModalSubmit) {
        onModalSubmit();
      } else if (!fromDonatePage) {
        navigate('/usuari');
      }
    } catch (error) {
      console.error('Error:', error);
      setError(
        error instanceof Error ? error.message : 'Error. Intenta-ho de nou.'
      );
      setIsSubmitting(false);
    }
  };

  const handleCreateAccount = () =>
    handleAction(async () => {
      /* No additional logic needed here */
    });
  const handleLogin = () =>
    handleAction(async () => {
      /* No additional logic needed here */
    });

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= MAX_NAME_LENGTH) setNewName(e.target.value);
  };

  const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length <= MAX_ID_LENGTH) setUserId(e.target.value);
  };

  const handleModeChange = (newMode: 'create' | 'login') => {
    setMode(newMode);
    setError('');
    setMessage(null);
    setUserId('');
  };

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={handleClose}
      maxWidth="md"
      className="relative"
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.8 }}
        transition={{ duration: 0.2 }}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-800 dark:text-white">
            Accedir
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <X size={20} />
          </button>
        </div>
        <div className="flex rounded-lg bg-gray-100 dark:bg-gray-700 p-1 mb-2">
          <button
            onClick={() => handleModeChange('create')}
            className={`flex-1 py-2 px-4 rounded-md transition-colors ${
              mode === 'create'
                ? 'bg-gradient-to-r from-yellow-500 to-red-500 text-white'
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              Crear Compte
            </motion.div>
          </button>
          <button
            onClick={() => handleModeChange('login')}
            className={`flex-1 py-2 px-4 rounded-md transition-colors ${
              mode === 'login'
                ? 'bg-gradient-to-r from-yellow-500 to-red-500 text-white'
                : 'text-gray-700 dark:text-gray-300'
            }`}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              Iniciar Sessió
            </motion.div>
          </button>
        </div>

        <AnimatePresence mode="wait" initial={false}>
          {mode === 'create' && (
            <motion.div
              key="create"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              ref={modalContentRef}
              className="space-y-4"
            >
              <div className="space-y-2 mb-4 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                <p>Introdueix el teu nom per crear un compte.</p>
                <p>
                  Pots canviar el teu nom quan ho necessitis i explorar la teva
                  pàgina, on trobaràs el teu minijoc preferit, les estadístiques
                  del teu progrés i altres opcions per gestionar el teu perfil.
                </p>
                <p>Gràcies per formar part d'aquesta aventura!</p>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleCreateAccount();
                }}
                className="space-y-4"
              >
                <div>
                  <input
                    type="text"
                    value={newName}
                    onChange={handleNameChange}
                    placeholder={defaultName}
                    className="w-full p-3 border-2 border-yellow-500 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white text-lg focus:outline-none"
                    required
                    maxLength={MAX_NAME_LENGTH}
                  />
                  <p className="!mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {newName.length}/{MAX_NAME_LENGTH} caràcters
                  </p>
                </div>
                {error && (
                  <p className="text-red-500 text-sm bg-red-100 dark:bg-red-900/30 p-2.5 rounded-lg">
                    {error}
                  </p>
                )}
                <div className="pt-2">
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-gradient-to-r from-yellow-500 to-red-500 hover:from-yellow-600 hover:to-red-600 text-white font-bold py-2.5 px-4 rounded-lg transition duration-300 text-base"
                  >
                    Acceptar
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}
          {mode === 'login' && (
            <motion.div
              key="login"
              variants={contentVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              ref={modalContentRef}
              className="space-y-4"
            >
              <div className="space-y-2 mb-4 text-sm sm:text-base text-gray-600 dark:text-gray-300">
                <p>
                  Introdueix el teu nom i ID per vincular les teves donacions al
                  teu compte i participar al Top de Donacions.
                </p>
                <p>
                  Si ja has donat abans, utilitza el mateix ID de la teva pàgina
                  d'usuari per associar les noves donacions al teu compte. Pots
                  trobar l'ID fent clic al botó{' '}
                  <span className="inline-flex items-center">
                    {'"'}
                    <Copy
                      size={16}
                      className="inline-block align-text-bottom mx-1"
                    />
                    Copiar ID
                    {'"'}
                  </span>{' '}
                  dins de la teva pàgina d'usuari.
                </p>
                <p>
                  És important no compartir el teu ID amb ningú, ja que serveix
                  per garantir que totes les teves donacions s'acumulin
                  correctament al teu perfil.
                </p>
              </div>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleLogin();
                }}
                className="space-y-4"
              >
                <div>
                  <input
                    type="text"
                    value={newName}
                    onChange={handleNameChange}
                    placeholder={defaultName}
                    className="w-full p-3 border-2 border-yellow-500 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white text-lg focus:outline-none"
                    required
                    maxLength={MAX_NAME_LENGTH}
                  />
                  <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                    {newName.length}/{MAX_NAME_LENGTH} caràcters
                  </p>
                </div>
                <div className="relative">
                  <input
                    type="text"
                    value={userId}
                    onChange={handleIdChange}
                    placeholder={defaultId}
                    className="w-full p-3 border-2 border-yellow-500 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white text-lg focus:outline-none"
                    required
                    maxLength={MAX_ID_LENGTH}
                  />
                  <button
                    type="button"
                    onClick={handlePasteCode}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1.5 rounded-lg bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 transition-colors"
                    aria-label="Enganxar codi"
                  >
                    <Clipboard size={16} />
                  </button>
                </div>
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  {userId.length}/{MAX_ID_LENGTH} caràcters
                </p>
                {error && (
                  <p className="text-red-500 text-sm bg-red-100 dark:bg-red-900/30 p-2.5 rounded-lg">
                    {error}
                  </p>
                )}
                <AnimatePresence>
                  {message && (
                    <motion.p
                      key={message.id}
                      variants={messageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className={`text-sm p-2.5 rounded-lg ${
                        message.type === 'error'
                          ? 'text-red-500 bg-red-50 dark:bg-red-900/30'
                          : 'text-green-500 bg-green-100 dark:bg-green-900/30'
                      }`}
                    >
                      {message.text}
                    </motion.p>
                  )}
                </AnimatePresence>
                <div className="pt-2">
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-gradient-to-r from-yellow-500 to-red-500 hover:from-yellow-600 hover:to-red-600 text-white font-bold py-2.5 px-4 rounded-lg transition duration-300 text-base"
                  >
                    Acceptar
                  </motion.button>
                </div>
              </form>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </BaseModal>
  );
};

export default IdentityModal;
